// Select Model - Universal Single/Multi Select (Valtio)
import {proxy, ref} from "valtio";

import {IQueryParamProvider} from "@/shared/contracts";
import {ValidateInterface} from "@/shared/contracts/validate.interface";
import {EventEmitter} from "@/shared/event-emitter";

import {ValidationError} from "../fields/types";
import {SelectConfig, SelectFieldEvents, SelectMultiConfig, SelectOption, SelectSingleConfig, SelectValidationResult, SelectValue} from "./types";

class SelectClass<T = string, O extends SelectOption<T> = SelectOption<T>, M extends boolean = false>
    implements IQueryParamProvider, ValidateInterface
{
    // Reactive state
    options: Array<O> = [];
    selected: Array<O> = [];
    searchQuery: string = "";
    isOpen: boolean = false;
    disabled: boolean = false;
    loading: boolean = false;
    errors: Array<ValidationError> = [];

    // Configuration
    readonly multiple: M;
    readonly isRequiredValue: boolean;
    readonly requireMessage: string;
    readonly maxSelected?: number;
    readonly isClearable: boolean;
    readonly isSearchable: boolean;
    readonly events: EventEmitter<SelectFieldEvents<T, O, M>>;

    constructor(config: SelectConfig<T, O, M>) {
        this.multiple = (config.multiple || false) as M;
        this.options = config.options || [];
        this.isRequiredValue = config.isRequired || false;
        this.requireMessage = config.requireMessage || "required";
        this.maxSelected = "maxSelected" in config ? config.maxSelected : undefined;
        this.isClearable = config.isClearable !== false; // default true
        this.isSearchable = config.isSearchable !== false; // default true
        this.disabled = config.disabled || false;

        this.events = ref(new EventEmitter<SelectFieldEvents<T, O, M>>()) as EventEmitter<SelectFieldEvents<T, O, M>>;

        if (config.initialValue !== undefined && config.initialValue !== null) {
            this.setSelectedByValue(config.initialValue as T | Array<T>);
        }
    }

    // Computed getters
    get value(): SelectValue<T, M> {
        if (this.multiple) {
            return this.selected.map((opt) => opt.value) as SelectValue<T, M>;
        }
        return (this.selected[0]?.value ?? null) as SelectValue<T, M>;
    }

    get selectedOption(): O | null {
        return this.selected[0] || null;
    }

    get selectedOptions(): Array<O> {
        return this.selected;
    }

    get selectedValues(): Array<T> {
        return this.selected.map((opt) => opt.value);
    }

    get filteredOptions(): Array<O> {
        if (!this.searchQuery.trim()) {
            return this.options;
        }

        const query = this.searchQuery.toLowerCase();
        return this.options.filter((option) => option.label.toLowerCase().includes(query));
    }

    get availableOptions(): Array<O> {
        return this.options.filter((opt) => !opt.disabled);
    }

    get canSelectMore(): boolean {
        if (!this.multiple) return this.selected.length === 0;
        if (!this.maxSelected) return true;
        return this.selected.length < this.maxSelected;
    }

    get isValid(): boolean {
        return this.validate().isValid;
    }

    get hasError(): boolean {
        return this.errors.length > 0;
    }

    get shouldShowError(): boolean {
        return this.hasError;
    }

    get error(): ValidationError | null {
        return this.errors[0] || null;
    }

    get isRequired(): boolean {
        return this.isRequiredValue;
    }

    get isSearchableField(): boolean {
        return this.isSearchable;
    }

    get isClearableField(): boolean {
        return this.isClearable;
    }

    get isMultiple(): boolean {
        return this.multiple;
    }

    // Get query string with parameter name
    getQueryParam(paramName: string): string {
        if (this.multiple) {
            // For multi-select, join values with comma: param=value1,value2,value3
            const values = this.selectedValues;
            if (values.length === 0) return "";
            const encodedValues = values.map((v) => encodeURIComponent(String(v))).join(",");
            return `${paramName}=${encodedValues}`;
        } else {
            // For single select: param=value
            const value = this.value;
            if (value === null || value === undefined) return "";
            return `${paramName}=${encodeURIComponent(String(value))}`;
        }
    }

    // Set value from query param string
    setFromQueryParam(paramValue: string | null | undefined): void {
        if (!paramValue) {
            this.selected = [];
            return;
        }

        const decoded = decodeURIComponent(paramValue);

        if (this.multiple) {
            // For multi-select, split by comma
            const values = decoded
                .split(",")
                .map((v) => v.trim())
                .filter((v) => v);
            this.setSelectedByValue(values as Array<T>);
        } else {
            // For single select
            this.setSelectedByValue(decoded as T);
        }
    }

    // Set value from URLSearchParams
    setFromQueryParams(params: URLSearchParams, paramName: string): void {
        const paramValue = params.get(paramName);
        this.setFromQueryParam(paramValue);
    }

    // Search
    setSearchQuery(query: string): void {
        this.searchQuery = query;
        this.events.emit("search", query);
    }

    // Selection methods
    select(option: O): void {
        if (!option || option.disabled) return;

        if (this.multiple) {
            // Multi-select logic
            if (!this.isSelected(option)) {
                if (this.canSelectMore) {
                    this.selected = [...this.selected, option];
                    this.events.emit("select", option);
                    this.events.emit("change", this.value);
                }
            }
        } else {
            // Single-select logic
            this.selected = [option];
            this.close();
            this.events.emit("select", option);
            this.events.emit("change", this.value);
        }
        this.errors = [];
    }

    deselect(option: O): void {
        if (!this.isClearable) return;

        const wasSelected = this.isSelected(option);
        this.selected = this.selected.filter((s) => s.value !== option.value);

        if (wasSelected) {
            this.events.emit("remove", option);
            this.events.emit("change", this.value);
        }
    }

    toggleOption(option: O): void {
        if (this.isSelected(option)) {
            this.deselect(option);
        } else {
            this.select(option);
        }
    }

    selectAll(): void {
        if (!this.multiple) return;

        const availableOptions = this.options.filter((opt) => !opt.disabled);

        if (this.maxSelected) {
            this.selected = availableOptions.slice(0, this.maxSelected);
        } else {
            this.selected = availableOptions;
        }

        this.errors = [];
        this.events.emit("change", this.value);
    }

    deselectAll(): void {
        if (!this.isClearable) return;

        this.selected = [];
        this.events.emit("change", this.value);
    }

    setSelectedByValue(value: T | Array<T>): void {
        if (this.multiple && Array.isArray(value)) {
            this.selected = value.map((v) => this.findOptionByValue(v)).filter((option): option is O => option !== undefined);
        } else if (!this.multiple && !Array.isArray(value)) {
            const option = this.findOptionByValue(value);
            this.selected = option ? [option] : [];
        }
    }

    // Dropdown control
    open(): void {
        if (!this.disabled && !this.isOpen) {
            this.isOpen = true;
            this.events.emit("open");
        }
    }

    close(): void {
        if (this.isOpen) {
            this.isOpen = false;
            this.events.emit("close");
        }
        this.searchQuery = "";
    }

    toggle(): void {
        if (this.isOpen) {
            this.close();
        } else {
            this.open();
        }
    }

    // Options management
    setOptions(options: Array<O>): void {
        this.options = options;
        // Remove selected options that no longer exist
        this.selected = this.selected.filter((selected) => options.some((opt) => opt.value === selected.value));
    }

    addOption(option: O): void {
        if (!this.options.some((opt) => opt.value === option.value)) {
            this.options = [...this.options, option];
        }
    }

    removeOption(option: O): void {
        this.options = this.options.filter((opt) => opt.value !== option.value);
        this.deselect(option);
    }

    // State management
    setDisabled(disabled: boolean): void {
        this.disabled = disabled;
        if (disabled) {
            this.close();
        }
    }

    disable(): void {
        this.setDisabled(true);
    }

    enable(): void {
        this.setDisabled(false);
    }

    setLoading(loading: boolean): void {
        this.loading = loading;
    }

    setError(error: ValidationError | null): void {
        if (error === null) {
            this.errors = [];
        } else {
            this.errors = [error];
        }
    }

    setErrors(errors: Array<ValidationError>): void {
        this.errors = errors;
    }

    clear(): void {
        if (!this.isClearable) return;

        this.selected = [];
        this.errors = [];
        this.events.emit("clear");
    }

    // Validation
    validate(): SelectValidationResult {
        if (this.isRequiredValue && this.selected.length === 0) {
            const error: ValidationError = {
                type: "error",
                message: this.requireMessage,
            };
            this.errors = [error];
            return {isValid: false, error: this.requireMessage};
        }
        this.errors = [];
        return {isValid: true};
    }

    // Event handlers
    onBlur(): void {
        this.close();
        this.validate();
    }

    onFocus(): void {
        this.open();
    }

    // Helpers
    isSelected(option: O): boolean {
        return this.selected.some((selected) => selected.value === option.value);
    }

    findOptionByValue(value: T): O | undefined {
        return this.options.find((opt) => opt.value === value);
    }

    // Scroll into view
    scrollIntoView(): void {
        this.events.emit("scrollIntoView");
    }
}

// Export type
export type SelectInstance<T = string, O extends SelectOption<T> = SelectOption<T>, M extends boolean = false> = SelectClass<T, O, M>;

// Factory function with overloads (SSR compatible)
export function Select<T = string, O extends SelectOption<T> = SelectOption<T>>(config: SelectSingleConfig<T, O>): SelectInstance<T, O, false>;

export function Select<T = string, O extends SelectOption<T> = SelectOption<T>>(config: SelectMultiConfig<T, O>): SelectInstance<T, O, true>;

export function Select<T = string, O extends SelectOption<T> = SelectOption<T>, M extends boolean = false>(
    config: SelectConfig<T, O, M>,
): SelectInstance<T, O, M> {
    return proxy<SelectInstance<T, O, M>>(new SelectClass<T, O, M>(config));
}
